import React, {Fragment, useContext, useState} from "react";
import {useQueryClient} from "react-query";
import axios from "axios";
import __ from "lodash";
import {FormControl, FormGroup, Grid, Tooltip, useTheme} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/CheckCircle";
import PendingIcon from '@mui/icons-material/Pending';
import SystemContext from "../../../SystemContext";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import {formatChars} from "../../../blocks/FormatCharacters";
import {
  FormDialog,
  ComboSelect,
  LabelledPaper,
  MaskedText,
  UserRoleSelect,
  FormCheckbox,
  Cropper
} from "../../../controls";
import companyUserValidator from "../validators/CompanyUserValidator";
import { useAddCompanyUser, useUpdateCompanyUser} from "../../../services/auth";

const apiSitePrefix = getApiSitePrefix();

const paperProps = {
  style: {
    backgroundColor: "#f8f8f8",
    boxShadow: 'none',
  }
}

const defaultLabelledPaperProps = {
  checkChildren: true,
  style: {display: "block", border: "solid 1px #b9b9b9" },
  labelStyle: {background: "#f0f0f0"},
  collapsible: false,
}

const UserDetailsForm = (_props) => {
  const props = {
    canEdit: false,
    ..._props,
  }
  const classes = props.classes;

  const [dialogStates, setDialogStates] = useState({...props.dialogStates});
  const [record, setRecord] = useState(props.record);
  const [editing, setEditing] = useState(props.newUser || false);
  const [newUser, setNewUser] = useState(props.newUser || false);
  const [avatar, setAvatar] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: "",
  });

  const SystemData = useContext(SystemContext);
  const theme = useTheme();

  const queryClient = useQueryClient();
  const handleUpdateCompanyUser = useUpdateCompanyUser();
  const handleAddCompanyUser = useAddCompanyUser();


  if ((props.record && !record) || (!props.record && record) || (props.record && record && props.record.name !== record.name) ) {
    setRecord(props.record);
    setEditing(true);
  }

  const title = newUser ? `New System User` : `User Details ${record.lastName ? "for " + record.firstName + " " + record.lastName: ""}`;

  const uploadAvatarToS3 = async (user, fileName, image) => {
    const headers = {...axios.defaults.headers, 'Content-Type': 'application/json'};
    try {
      const response = await axios.post(
        `${apiSitePrefix}/useravatar/upload/${props.user}/${fileName}`,
        { document: JSON.stringify(image) },
        { headers }
      );

      if (response && response.data) {
        if (response.data.status) {
          SystemData.openAlert("Avatar Upload", "Avatar image Upload completed successfully");
          return (response.data.url);
        } else {
          SystemData.openAlert("Avatar Upload Error", "The Avatar image failed to upload with this error: " + response.data.error);
        }
      } else {
        SystemData.openAlert("Avatar Upload Error", "The Avatar image failed to upload due to an unknown error");
      }
    } catch (err) {
      console.error(err);
      SystemData.openAlert("Avatar Upload Error", "The Avatar image failed to upload with this error: " + err);
    }

    return "";
  }

  const getButtons = () => {
    if (props.canEdit) {
      if (editing) {
        return (
          {
            save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{return status.error}},
            cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"},
          }
        );
      }
      if (newUser) {
        return (
          {
            save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{return status.error}},
            cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
          }
        );
      }
      return (
        {
          edit: {icon: "edit", key: "Edit", label: "Edit", color: "primary"},
          cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
        }
      )
    }
    return (
      {
        cancel:{icon: "cancel", key: "Close", label: "Close", color: "primary"}
      }
    );
  }


  return (
    <div key="formContainer" style={{width: "100%", textAlign:"left" }}>
      {record ?
        <FormDialog
          key="user details form"
          title={title}
          message=""
          open={dialogStates.showDialog}
          dialogOptions={{maxWidth: "lg", fullWidth: true, PaperProps: paperProps}}
          record={record}
          validator={companyUserValidator}
          validatorPosition="top"
          alert={alert}
          formHeightOffset={320}
          titleBarStyle={{backgroundColor: '#3f3f3f', color: theme.palette.primary.main}}
          buttonBarStyleOverride={{
            position: "relative",
            width: "calc(100% - 15px)",
            backgroundColor: "#ffffff",
            borderTop: `1px solid ${props.fullPageStyle ? "grey" : "lightgrey"}`, ...props.fullPageStyle
          }}
          formBodyStyle={{...props.fullPageStyle}}
          style={{...props.fullPageStyle}}
          buttons={getButtons()}
          onChange={async (state) => {
            if (state.buttonPressed === "edit" && props.canEdit) {
              setEditing(true);
            }
            if (state.buttonPressed === "cancel") {
              setEditing(false);
              setRecord(props.record);
              setDialogStates({showDialog: false});
              if (props.close)
                props.close();
            }
            if (state.buttonPressed === "save") {
              SystemData.toggleLoadingProgress(true);

              let newPicture = null;
              if (avatar) {
                newPicture = await uploadAvatarToS3(state.record.name, avatar.fileName, avatar.image);
              }

              if (newUser) {
                handleAddCompanyUser.mutate({
                  extraKeys:[record.companyUuid],
                  record: {...state.record, picture: newPicture ?? props.record.picture},
                  postSuccess: (data) => {
                    SystemData.toggleLoadingProgress(false);
                    if (data && __.has(data, "record")) {
                      const response = data.record.data;
                      if (response.success) {
                        setEditing(false);
                        setNewUser(false);
                        setAlert({severity: "info", open: true, message: `New user account created for ${state.record.firstName} ${state.record.lastName}`, timeOut: 5});
                        state.recordChange( {...state.record, picture: newPicture ?? props.record.picture});
                      } else {
                        SystemData.alert("Error", `Could not create user account for ${state.record.firstName} ${state.record.lastName}: ${response.error}`);
                      }
                    }
                    queryClient.invalidateQueries(["CompanyUsers", record.companyUuid]);
                  },
                  postError: (data) => {
                    SystemData.toggleLoadingProgress(false);
                    if (data.errorMessage) {
                      SystemData.alert("Error", `${data.errorMessage}`);
                    } else {
                      SystemData.alert("Error", `An Unexpected Error has occurred creating user account for ${state.record.name}`);
                    }
                    queryClient.invalidateQueries(["CompanyUsers", record.companyUuid]);
                  }
                });
              } else {
                handleUpdateCompanyUser.mutate({
                  extraKeys:[record.companyUuid],
                  record: {...state.record, picture: newPicture ?? props.record.picture},
                  oldRecord: props.record,
                  postSuccess: () => {
                    setEditing(false);
                    SystemData.toggleLoadingProgress(false);
                    setAlert({severity: "info", open: true, message: `${state.record.name} Updated`, timeOut: 5});
                    state.recordChange( {...state.record, picture: newPicture ?? props.record.picture});
                    getButtons();
                  },
                  postError: (data) => {
                    SystemData.toggleLoadingProgress(false);
                    if (data.errorMessage) {
                      SystemData.alert("Error", `${data.errorMessage}`);
                    } else {
                      SystemData.alert("Error", `An Unexpected Error has occurred updating ${state.record.name}`);
                    }
                  }
                });
                SystemData.toggleLoadingProgress(false);
              }
            } else {
            }
          }}
        >
          <Grid container checkChildren={true} spacing={3}>

            <Grid item xs={12} checkChildren={true}>
              <LabelledPaper {...defaultLabelledPaperProps} label="User Details" >
                <FormControl checkChildren={true} key="FC-main" style={{display: "block" }}>
                  <FormGroup checkChildren={true} key="FG-main" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={6} checkChildren={true}>
                        <Grid container checkChildren={true} spacing={3} direction="column" justify="center">
                          <Grid item xs={12} />
                          <Grid item xs={12} checkChildren={true} >
                            {record => { return (
                              editing
                                ?
                                  // <AvatarEdit
                                  //   newUser={newUser}
                                  //   src={record.picture}
                                  //   img={record.picture}
                                  //   previewWidth={150}
                                  //   width={200}
                                  //   height={200}
                                  //   user={record.name}
                                  //   onChange={(src, props)=> {
                                  //     //debugger;
                                  //     const newData = {
                                  //       ...props.record,
                                  //       picture: src
                                  //     }
                                  //     props.recordChange(newData);
                                  //   }}
                                  // />
                                  <Cropper
                                    newUser={newUser}
                                    src={record.picture}
                                    img={record.picture}
                                    onChange={(croppedAvatar, props)=> {
                                      setAvatar(croppedAvatar);
                                      // const newData = {
                                      //   ...props.record,
                                      //   picture: src
                                      // }
                                      // props.recordChange(newData);
                                    }}
                                  />
                                :
                                <Grid container checkChildren={true} spacing={3} direction="row" justify="center">
                                  <Grid item xs={1} />
                                    <Grid item xs={11} checkChildren={true} >
                                      <img src={record.picture} alt="Preview" style={{width: 150, borderRadius: '50%'}}/>
                                    </Grid>
                                </Grid>
                            )}}
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={6} checkChildren={true}>
                        <Grid container checkChildren={true} spacing={3}>
                          <Grid item xs={6} checkChildren={true}>
                            <MaskedText
                              id="firstName"
                              label="First Name"
                              readOnly={editing!==true}
                              inputLabelProps={{shrink: true}}
                              fullWidth
                              mask={"x".repeat(50)}
                              maskChar={""}
                              alwaysShowMask={false}
                              formatChars={formatChars}
                            />
                          </Grid>
                          <Grid item xs={6} checkChildren={true}>
                            <MaskedText
                              id="lastName"
                              label="Last Name"
                              readOnly={editing!==true}
                              inputLabelProps={{shrink: true}}
                              fullWidth
                              mask={"x".repeat(50)}
                              maskChar={""}
                              alwaysShowMask={false}
                              formatChars={formatChars}
                            />
                          </Grid>
                          <Grid item xs={12}/>
                          <Grid item xs={12} checkChildren={true}>
                            <Grid container checkChildren={true} spacing={3}>
                              <Grid item xs={6} checkChildren={true} style={{display: "flex"}}>
                                {record => (
                                  <Fragment checkChildren={true}>
                                    <MaskedText
                                      id="email"
                                      label="User Email"
                                      readOnly={editing !== true}
                                      inputLabelProps={{shrink: true}}
                                      fullWidth
                                      mask={"x".repeat(50)}
                                      maskChar={""}
                                      alwaysShowMask={false}
                                      formatChars={formatChars}
                                    />
                                    {record.email_verified
                                      ?
                                        <CheckIcon></CheckIcon>
                                      :
                                        <Tooltip title={"Email verification pending"}>
                                          <PendingIcon></PendingIcon>
                                        </Tooltip>
                                    }
                                  </Fragment>)
                                }
                              </Grid>
                              <Grid item xs={6} checkChildren={true}>
                                <FormControl checkChildren={true} style={{display: "block"}}>
                                  <ComboSelect
                                    id="department"
                                    label="Department"
                                    inputLabelProps={{shrink: true}}
                                    group="DEPARTMENTS"
                                    subGroup=""
                                    readOnly={editing!==true}
                                    fullWidth
                                    //style={{marginTop: "2px"}}
                                  />
                                </FormControl>
                              </Grid>
                              <Grid item xs={6} checkChildren={true}>
                                <FormControl checkChildren={true} style={{display: "block"}}>
                                  <ComboSelect
                                    id="contactRole"
                                    label="User Contact Role"
                                    inputLabelProps={{shrink: true}}
                                    group="CONTACT ROLES"
                                    subGroup=""
                                    readOnly={editing!==true}
                                    fullWidth
                                    //style={{marginTop: "2px"}}
                                  />
                                </FormControl>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} checkChildren={true}>
                            {newUser
                              ?
                              <Grid container checkChildren={true} spacing={3}>
                                <Grid item xs={12} />
                                <Grid item xs={6} checkChildren={true}>
                                  <MaskedText
                                    id="password"
                                    label="Password"
                                    readOnly={editing!==true}
                                    inputLabelProps={{shrink: true}}
                                    fullWidth
                                    mask={"x".repeat(50)}
                                    maskChar={""}
                                    alwaysShowMask={false}
                                    formatChars={formatChars}
                                    type="password"
                                  />
                                </Grid>
                                <Grid item xs={6} checkChildren={true}>
                                  <MaskedText
                                    id="repeatPassword"
                                    label="Repeat Password"
                                    readOnly={editing!==true}
                                    inputLabelProps={{shrink: true}}
                                    fullWidth
                                    mask={"x".repeat(50)}
                                    maskChar={""}
                                    alwaysShowMask={false}
                                    formatChars={formatChars}
                                    type="password"
                                  />
                                </Grid>
                              </Grid>
                              :
                              <></>
                            }
                          </Grid>
                          <Grid item xs={12} checkChildren={true}>
                            <Grid container checkChildren={true} spacing={3}>
                              <Grid item xs={6} checkChildren={true}>
                                <MaskedText
                                  id="last_ip"
                                  label="Last IP"
                                  readOnly={true}
                                  inputLabelProps={{shrink: true}}
                                  fullWidth
                                  mask={"x".repeat(50)}
                                  maskChar={""}
                                  alwaysShowMask={false}
                                  formatChars={formatChars}
                                />
                              </Grid>
                              <Grid item xs={6} checkChildren={true}>
                                <MaskedText
                                  id="lastLogin"
                                  label="Last Login"
                                  readOnly={true}
                                  inputLabelProps={{shrink: true}}
                                  fullWidth
                                  mask={"x".repeat(50)}
                                  maskChar={""}
                                  alwaysShowMask={false}
                                  formatChars={formatChars}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            <Grid item xs={12}/>
            <Grid item xs={12} checkChildren={true}>
              <LabelledPaper {...defaultLabelledPaperProps} label="Assigned System Roles" >
                <FormControl checkChildren={true} key="FC-roles" style={{display: "block" }}>
                  <FormGroup checkChildren={true} key="FG-roles" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={12} checkChildren={true}>
                        <FormControl checkChildren={true} style={{display: "block"}}>
                          {record =>
                            <UserRoleSelect
                              id="roles"
                              label="Roles"
                              values={record.roles || []}
                              readOnly={editing!==true}
                              //fullWidth
                              onChange={(values, props) => {
                                const data = {
                                  ...props.record,
                                  roles: values
                                }
                                props.recordChange(data);
                              }}
                            />
                          }
                        </FormControl>
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            <Grid item xs={12}/>
            <Grid item xs={12} checkChildren={true}>
              <LabelledPaper {...defaultLabelledPaperProps} label="Status" >
                <FormControl checkChildren={true} key="FC-status" style={{display: "block" }}>
                  <FormGroup checkChildren={true} key="FG-status" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={3} checkChildren={true}>
                        <FormControl checkChildren={true} style={{display: "block"}}>
                          {record =>
                            <FormCheckbox
                              id="blocked"
                              key="blocked"
                              label="Block User"
                              className={classes.checkbox}
                              readOnly={editing!==true}
                              checked={record.blocked}
                            />
                          }
                        </FormControl>
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

          </Grid>
        </FormDialog>
        :
        <></>
      }
    </div>
  )
}

export default UserDetailsForm;
